"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { SplitText } from "gsap/SplitText";
import styles from "./Preloader.module.css";

gsap.registerPlugin(CustomEase, SplitText);

const START_DELAY_S = 1;
const INTRO_SCALE = 0.2;
const INTRO_GAP = 40;
const INTRO_ROTATIONS = [-15, 5, -7.5, 10, -2.5];

const IMAGES = [
  "/pictures/2planes.jpg",
  "/pictures/face-changing.jpg",
  "/pictures/landscape.jpg",
  "/pictures/thor.jpg",
  "/pictures/tunes.jpg",
];

export default function PreloaderHero() {
  const rootRef = useRef<HTMLDivElement>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const root = rootRef.current;
    if (!root) return;

    CustomEase.create("hop", "0.9, 0, 0.1, 1");
    CustomEase.create("glide", "0.8, 0, 0.2, 1");

    const introImages = gsap.utils.toArray<HTMLElement>(
      root.querySelectorAll(`.${styles.introImg}`),
    );
    const heroImg = root.querySelector<HTMLElement>(`.${styles.heroImg}`);
    const preloader = root.querySelector<HTMLElement>(`.${styles.preloader}`);
    const preloaderOverlay = root.querySelector<HTMLElement>(
      `.${styles.preloaderOverlay}`,
    );
    // Lock scroll while preloader plays
    document.documentElement.style.overflow = "hidden";

    // --- position math (recomputable) ---
    const computeLayout = () => {
      const vw = window.innerWidth;
      const scaledWidth = vw * INTRO_SCALE;
      const rowWidth = scaledWidth * 5 + INTRO_GAP * 4;
      const centeredX = (vw - rowWidth) / 2;
      const offScreenX = centeredX - vw * 1.3;
      return { vw, scaledWidth, centeredX, offScreenX };
    };

    const applyInitial = () => {
      const { scaledWidth, centeredX, offScreenX, vw } = computeLayout();
      introImages.forEach((img, i) => {
        const targetCentered =
          centeredX + i * (scaledWidth + INTRO_GAP) + scaledWidth / 2 - vw / 2;
        const targetOff =
          offScreenX + i * (scaledWidth + INTRO_GAP) + scaledWidth / 2 - vw / 2;
        img.dataset.centeredX = String(targetCentered);
        gsap.set(img, {
          scale: INTRO_SCALE,
          x: targetOff,
          rotation: INTRO_ROTATIONS[i],
          borderRadius: "2.5rem",
        });
      });
    };

    const ctx = gsap.context(() => {
      applyInitial();

      const realSplit = SplitText.create(
        [
          ...Array.from(root.querySelectorAll(`.${styles.navItem}`)),
          root.querySelector(`.${styles.heroHeader} h1`),
          root.querySelector(`.${styles.heroSocial} p`),
          ...Array.from(root.querySelectorAll(`.${styles.heroSocial} a`)),
        ].filter(Boolean) as Element[],
        {
          type: "lines",
          linesClass: styles.line,
          mask: "lines",
          autoSplit: true,
        },
      );

      gsap.set(`.${styles.line}`, { y: "125%" });

      const tl = gsap.timeline({ delay: START_DELAY_S });

      tl.to(preloader, {
        scaleX: 1,
        duration: 1.5,
        ease: "glide",
        onComplete: () => {
          gsap.set(preloader, { transformOrigin: "right" });
        },
      });

      tl.to(preloader, { scaleX: 0, duration: 1.25, ease: "hop" });

      tl.to(
        preloaderOverlay,
        {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
          duration: 1,
          ease: "hop",
        },
        "<0.75",
      );

      introImages.forEach((img) => {
        tl.to(
          img,
          {
            x: () => parseFloat(img.dataset.centeredX || "0"),
            duration: 1.5,
            ease: "glide",
          },
          "<0.025",
        );
      });

      tl.addLabel("spread", ">");

      tl.to(
        [introImages[0], introImages[1]],
        { x: "-100vw", duration: 1.5, ease: "glide" },
        "spread",
      );
      tl.to(
        [introImages[3], introImages[4]],
        { x: "100vw", duration: 1.5, ease: "glide" },
        "spread",
      );

      tl.to(
        heroImg,
        {
          scale: 1,
          x: 0,
          rotation: 0,
          borderRadius: 0,
          duration: 1.5,
          ease: "glide",
        },
        "<",
      );

      tl.to(
        root.querySelectorAll(`nav .${styles.line}`),
        { y: "0%", duration: 1, stagger: 0.1, ease: "power3.out" },
        "<1",
      );

      tl.to(
        root.querySelectorAll(`.${styles.heroHeader} .${styles.line}`),
        { y: "0%", duration: 1, stagger: 0.1, ease: "power3.out" },
        "<",
      );

      tl.to(
        root.querySelectorAll(`.${styles.heroSocial} .${styles.line}`),
        { y: "0%", duration: 1, stagger: 0.1, ease: "power3.out" },
        "<0.25",
      );

      // Recalc only the OFF/CENTERED targets on resize.
      // Off-screen images already use vw, so only re-pin the centered targets.
      const onResize = () => {
        const { scaledWidth, centeredX, vw } = computeLayout();
        introImages.forEach((img, i) => {
          const targetCentered =
            centeredX +
            i * (scaledWidth + INTRO_GAP) +
            scaledWidth / 2 -
            vw / 2;
          img.dataset.centeredX = String(targetCentered);
        });
        // keep the hero image full-bleed if animation already finished
        if (tl.progress() === 1 && heroImg) {
          gsap.set(heroImg, { x: 0, scale: 1, rotation: 0, borderRadius: 0 });
        }
      };

      window.addEventListener("resize", onResize);

      tl.eventCallback("onComplete", () => {
        document.documentElement.style.overflow = "";
        // Overlay no longer needed
        if (preloaderOverlay) preloaderOverlay.style.display = "none";
        // Subtle scroll down once animation finishes
        window.scrollTo({ top: 600, behavior: "smooth" });
      });

      return () => {
        window.removeEventListener("resize", onResize);
        tl.kill();
        realSplit.revert();
      };
    }, root);

    return () => {
      ctx.revert();
      document.documentElement.style.overflow = "";
    };
  }, []);

  return (
    <div ref={rootRef}>
      {/* Preloader overlay — on top of everything */}
      <div className={styles.preloaderOverlay}>
        <div className={styles.preloader} />
      </div>

      <section className={styles.hero}>
        {IMAGES.map((src, i) => {
          const isHero = i === 2;
          return (
            <div
              key={src}
              className={`${styles.introImg} ${isHero ? styles.heroImg : ""}`}
              style={
                isHero
                  ? {
                      backgroundColor: "#0a0a0a",
                    }
                  : undefined
              }
            >
              {!isHero && <img src={src} alt="" />}
            </div>
          );
        })}
      </section>
    </div>
  );
}
